import { ThemeOptions, darken, lighten, alpha } from '@mui/material';

// Colors

const neutral = {
  100: '#F3F4F6',
  200: '#dee0e3',
  300: '#c2c4c7',
  400: '#a1a3a9',
  500: '#7e8188',
  600: '#5e6168',
  700: '#42444d',
  800: '#2b2d37',
  900: '#1C1F28',
};

const background = {
  default: '#101217',
  paper: neutral[900],
};

const divider = '#292b33';

const primary = {
  main: '#FACF3A',
  light: '#FBD861',
  dark: '#AF9028',
  contrastText: '#000000',
};

const secondary = {
  main: '#FF9800',
  light: '#FFB74D',
  dark: '#F57C00',
  contrastText: neutral[900],
};

const success = {
  main: '#14B8A6',
  light: '#43C6B7',
  dark: '#0E8074',
  contrastText: neutral[900],
};

const info = {
  main: '#2196F3',
  light: '#64B6F7',
  dark: '#0B79D0',
  contrastText: neutral[900],
};

const warning = {
  main: '#FFB020',
  light: '#FFBF4C',
  dark: '#B27B16',
  contrastText: neutral[900],
};

const error = {
  main: '#D14343',
  light: '#DA6868',
  dark: '#922E2E',
  contrastText: neutral[900],
};

const text = {
  primary: '#EDF2F7',
  secondary: '#A0AEC0',
  disabled: 'rgba(255, 255, 255, 0.48)',
};

// COLOR
// other color
const other = {
  main: '#292b33',
  outlinedBorder: '#292b33',
  backdrop: 'rgba(0, 0, 0, 0.5)',
  divider: '#292b33',
  snackbarBackground: '#FFFFFF',
  rating: '#FFB400',
};

// CUSTOM COLOR
// 중요도가 떨어지는 액션에 무채색 배정
const trivialAction = {
  main: text.secondary,
  light: lighten(text.secondary, 0.2),
  dark: darken(text.secondary, 0.2),
  contrastText: neutral[900],
};

// 관심에서 벗어난 상태를 뜻함 (info/warning/error 와 동급)
const inactiveStatus = {
  main: 'rgba(255, 255, 255, 0.26)',
  light: '#FBD861',
  dark: '#AF9028',
  contrastText: '#000',
};

// 교사 근태시 표시하는 컬러. light은 출근, dark는 퇴근쪽
const teacherClockInOut = {
  main: '#9281b2',
  light: '#9e8cbf',
  dark: '#695d80',
  contrastText: '#000',
};

const calendarBorder = {
  main: neutral[700],
  light: neutral[600],
  dark: neutral[800],
  contrastText: text.primary,
};

const cardOnPaper = {
  main: neutral[800],
  light: neutral[700],
  dark: neutral[900],
  contrastText: text.primary,
};

export const darkThemeOptions: ThemeOptions = {
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[500],
          color: text.primary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filledDefault': {
            'backgroundColor': neutral[800],
            '& .MuiChip-deleteIcon': {
              color: neutral[500],
            },
          },
          '&.MuiChip-outlinedDefault': {
            'borderColor': neutral[700],
            '& .MuiChip-deleteIcon': {
              color: neutral[700],
            },
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          scrollbarColor: `${alpha(neutral[500], 0.6)} ${alpha(neutral[800], 0.6)}`,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(neutral[500], 0.6),
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(neutral[800], 0.6),
            borderRadius: 4,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: divider,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: 'solid',
          borderWidth: 1,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: 'solid',
          borderWidth: 1,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: neutral[700],
        },
        track: {
          backgroundColor: neutral[500],
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${divider}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          'backgroundColor': neutral[800],
          '.MuiTableCell-root': {
            color: neutral[300],
          },
        },
      },
    },
  },
  palette: {
    action: {
      active: neutral[400],
      hover: 'rgba(255, 255, 255, 0.04)',
      selected: 'rgba(255, 255, 255, 0.08)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.26)',
    },
    background,
    divider,
    error,
    info,
    mode: 'dark',
    neutral,
    primary,
    secondary,
    success,
    text,
    warning,
    other,
    trivialAction,
    inactiveStatus,
    teacherClockInOut,
    calendarBorder,
    cardOnPaper,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 1px 4px rgba(0, 0, 0, 0.24)',
    '0px 1px 5px rgba(0, 0, 0, 0.24)',
    '0px 1px 6px rgba(0, 0, 0, 0.24)',
    '0px 2px 6px rgba(0, 0, 0, 0.24)',
    '0px 3px 6px rgba(0, 0, 0, 0.24)',
    '0px 4px 6px rgba(0, 0, 0, 0.24)',
    '0px 5px 12px rgba(0, 0, 0, 0.24)',
    '0px 5px 14px rgba(0, 0, 0, 0.24)',
    '0px 5px 15px rgba(0, 0, 0, 0.24)',
    '0px 6px 15px rgba(0, 0, 0, 0.24)',
    '0px 7px 15px rgba(0, 0, 0, 0.24)',
    '0px 8px 15px rgba(0, 0, 0, 0.24)',
    '0px 9px 15px rgba(0, 0, 0, 0.24)',
    '0px 10px 15px rgba(0, 0, 0, 0.24)',
    '0px 12px 22px -8px rgba(0, 0, 0, 0.24)',
    '0px 13px 22px -8px rgba(0, 0, 0, 0.24)',
    '0px 14px 24px -8px rgba(0, 0, 0, 0.24)',
    '0px 20px 25px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
  ],
};
